/**
 * Histogram Group By must follow P6 Resource Assignments cells:
 *   Day    = that day's qty_per_hr × calendar hours
 *   Week   = those days rolled to the chosen week start
 *   Month  = those days in the calendar month
 * Never sum Sunday-week bars into a month — a week of 29 Nov is November
 * in weekly view but Mon 30 Nov / Dec 1–5 split across two months in P6.
 */

export function groupBucketKey(dateStr, groupBy, weekStart = 'sunday') {
  const d = String(dateStr || '').slice(0, 10);
  if (!d) return '';
  if (groupBy === 'month') return d.slice(0, 7);
  if (groupBy === 'day') return d;
  const dt = new Date(`${d}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  if (weekStart === 'sunday') {
    dt.setDate(dt.getDate() - dt.getDay());
  } else {
    const back = dt.getDay() === 0 ? 6 : dt.getDay() - 1;
    dt.setDate(dt.getDate() - back);
  }
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function grainKeyOfBucket(bucket, groupBy, weekStart) {
  if (groupBy === 'month') return String(bucket || '').slice(0, 7);
  if (groupBy === 'day') return String(bucket || '').slice(0, 10);
  return groupBucketKey(bucket, 'week', weekStart);
}

/**
 * Hours in one Group By bucket from P6 assignment cells.
 * Prefers daily cells so week / month / day are the same as-is spread.
 */
export function hoursInGrain(resources, bucket, groupBy = 'week', weekStart = 'sunday') {
  const want = grainKeyOfBucket(bucket, groupBy, weekStart);
  if (!want) return 0;
  let hrs = 0;
  for (const r of resources || []) {
    const daily = r.dailySeries || [];
    if (daily.length) {
      for (const p of daily) {
        if (groupBucketKey(p.date, groupBy, weekStart) === want) {
          hrs += Number(p.period) || 0;
        }
      }
      continue;
    }
    if (groupBy === 'month' && (r.monthlySeries || []).length) {
      for (const p of r.monthlySeries) {
        if (String(p.date || '').slice(0, 7) === want) hrs += Number(p.period) || 0;
      }
      continue;
    }
    for (const p of r.series || []) {
      if (groupBucketKey(p.date, groupBy, weekStart) === want) {
        hrs += Number(p.period) || 0;
      }
    }
  }
  return hrs;
}

const roundHrs = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

function remainingHasStarted(rowDate, remainingFromWeek, groupBy) {
  if (!remainingFromWeek) return false;
  const d = String(rowDate || '').slice(0, 10);
  const from = String(remainingFromWeek).slice(0, 10);
  if (!d || !from) return false;
  if (groupBy === 'month') return d.slice(0, 7) >= from.slice(0, 7);
  return d >= from;
}

function afterDataDate(rowDate, { remainingFromWeek, dataDate, groupBy }) {
  const d = String(rowDate || '').slice(0, 10);
  if (!d) return false;
  if (groupBy === 'month') {
    const cut = String(dataDate || remainingFromWeek || '').slice(0, 7);
    return !!cut && d.slice(0, 7) > cut;
  }
  const cut = String(remainingFromWeek || dataDate || '').slice(0, 10);
  return !!cut && d > cut;
}

/**
 * Rebuild S-curve hour totals from the resources that are actually on the
 * chart. The API cumulatives are job-wide — a Resource ID filter must drop
 * the line to that resource's hours, not leave the full-job BAC.
 *
 * Cum. Remaining is earned-to-date + remaining after the data date (a
 * continuation of the earned curve). A remaining-only running sum from
 * zero is the regression that draws the yellow line off the origin.
 *
 * Cum. Earned stops at the data date — it must not keep plotting the last
 * total as a flat line through remaining. Remaining starts there.
 *
 * `hoursForRow` may return `actual: null` when the caller has no per-resource
 * spent hours (Maestro actuals are job-wide, not per P6 resource). The row's
 * API `actual_cumulative` is kept then instead of being flattened to zero.
 */
export function runningHourCumulatives(rows, hoursForRow, opts = {}) {
  const remainingFromWeek = opts.remainingFromWeek || null;
  const groupBy = opts.groupBy || 'week';
  const dataDate = opts.dataDate || null;
  let budget = 0;
  let earned = 0;
  let actual = 0;
  let remaining = 0;
  let remainingStarted = false;
  let lastEarnedIdx = -1;
  const out = (rows || []).map((row, i) => {
    const h = hoursForRow(row) || {};
    const remHrs = Number(h.remaining) || 0;
    const earnedHrs = Number(h.earned) || 0;
    const keepApiActual = h.actual == null;
    budget += Number(h.budget) || 0;
    earned += earnedHrs;
    actual += Number(h.actual) || 0;
    remaining += remHrs;
    if (Math.abs(earnedHrs) > 0.0005) lastEarnedIdx = i;
    if (remHrs > 0 || remainingHasStarted(row.date, remainingFromWeek, groupBy)) {
      remainingStarted = true;
    }
    const earnedDone = afterDataDate(row.date, { remainingFromWeek, dataDate, groupBy });
    return {
      ...row,
      budget_cumulative: roundHrs(budget),
      earned_cumulative: earnedDone ? null : roundHrs(earned),
      actual_cumulative: keepApiActual
        ? (row.actual_cumulative == null ? null : row.actual_cumulative)
        : roundHrs(actual),
      remaining_cumulative: remainingStarted ? roundHrs(earned + remaining) : null
    };
  });
  if (!remainingFromWeek && !dataDate && lastEarnedIdx >= 0) {
    for (let i = lastEarnedIdx + 1; i < out.length; i += 1) {
      out[i] = { ...out[i], earned_cumulative: null };
    }
  }
  // Join yellow to green one bucket earlier (same as the chart clip).
  const firstRem = out.findIndex((r) => r.remaining_cumulative != null);
  if (firstRem > 0 && out[firstRem - 1].earned_cumulative != null) {
    out[firstRem - 1] = {
      ...out[firstRem - 1],
      remaining_cumulative: roundHrs(out[firstRem - 1].earned_cumulative)
    };
  }
  return out;
}
